import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  HostListener,
  inject,
  Injector,
  input,
  output,
  signal,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import {
  DOWNLOAD_MESSAGE_ONE,
  DOWNLOAD_MESSAGE_THREE,
  DOWNLOAD_MESSAGE_TWO,
  DownloadsExportFacadeService,
  EXPORT_MESSAGE,
  ExportArgs,
  ExportRequest,
} from '@data-access/downloads-exports';
import { createProcessingData } from '@data-access/downloads-exports/utils';
import { buildExportData } from '@data-access/downloads-exports/utils/build-export-data.util';
import { WcgTableValues } from '@shared/interfaces/wcg-table-utils.interface';
import { WcgButtonComponent } from '@wcg/frontend-framework';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, WcgButtonComponent, MatMenuModule, MatIconModule],
  selector: 'wcg-grid-actions',
  styleUrls: ['grid-actions.component.scss'],
  template: `<div class="grid-actions__action-buttons">
    <div class="flex flex-grow">
      @let hasSelectedRows = $totalSelectedRows() > 0;
      @if (hasSelectedRows) {
        <span class="grid-actions__selected-counter mt-2">
          {{ $totalSelectedRows() }} Selected &nbsp;
          <span (click)="resetSelectedRows.emit()">&times;</span>
        </span>
      }

      <!--Download all documents button when no rows are selected-->
      @if (!hasSelectedRows && canDownload()) {
        <wcg-button
          class="grid-actions__download-btn mt-4 !ml-4"
          color="tertiary"
          size="slim"
          (click)="handleDownload()"
        >
          <mat-icon fontSet="material-icons-outlined">download</mat-icon>
          Download All</wcg-button
        >
      }
      <!--Download selected documents button when rows are selected-->
      @if (hasSelectedRows && canDownload()) {
        <wcg-button
          class="grid-actions__download-btn mt-4 !ml-4"
          color="tertiary"
          size="slim"
          (click)="handleDownload()"
        >
          <mat-icon fontSet="material-icons-outlined">download</mat-icon>
          Download</wcg-button
        >
      }

      @if (canExport()) {
        <wcg-button
          color="tertiary"
          size="slim"
          class="grid-actions__export-btn mt-4 !ml-4"
          [matMenuTriggerFor]="exportMenu"
          (click)="toggleExport($event)"
          data-testid="grid-actions__export-all-button"
        >
          Export All
          <mat-icon fontSet="material-icons-outlined">{{
            exportMenuOpen() ? 'keyboard_arrow_up' : 'keyboard_arrow_down'
          }}</mat-icon></wcg-button
        >

        <mat-menu #exportMenu="matMenu">
          <button
            mat-menu-item
            (click)="handleExport('csv')"
            data-testid="grid-actions__export-csv-button"
          >
            Export as CSV
          </button>
          <button
            mat-menu-item
            (click)="handleExport('pdf')"
            data-testid="grid-actions__export-pdf-button"
          >
            Export as PDF
          </button>
        </mat-menu>
      }

      @if (hasSelectedRows && canBulkEdit()) {
        <wcg-button
          class="grid-actions__edit-btn mt-4 !ml-4"
          color="tertiary"
          size="slim"
          (click)="bulkEdit.emit()"
          data-testid="grid-actions__bulk-edit-btn"
        >
          <mat-icon fontSet="material-icons-outlined">edit</mat-icon>
          Edit</wcg-button
        >
      }

      @if (hasSelectedRows && canDelete()) {
        <wcg-button
          class="grid-actions__delete-btn mt-4 !ml-4"
          color="tertiary-warn"
          size="slim"
          (click)="delete.emit()"
          data-testid="grid-actions__delete-btn"
        >
          <mat-icon fontSet="material-icons-outlined">delete</mat-icon
          >{{ deleteBtnLabel() }}</wcg-button
        >
      }
    </div>
  </div> `,
})
export class GridActionsComponent<T> {
  private readonly _injector = inject(Injector);
  private readonly _downloadsExportFacadeService = inject(
    DownloadsExportFacadeService,
  );
  readonly canExport = input<boolean>(true);
  readonly canDelete = input<boolean>(false);
  readonly canDownload = input<boolean>(false);
  readonly deleteBtnLabel = input<string>('Delete');
  readonly studyId = input<string>('');
  readonly entityId = input<string>('');
  readonly entityType = input<string>('');
  readonly isOutcome = input<boolean>(false);
  readonly exportArgs = input<ExportArgs | undefined>(undefined);
  readonly canBulkEdit = input<boolean>(false);
  readonly selectedRows = input<T[]>([]);
  readonly apiURL = input<string>('');
  readonly wcgTableValues = input<WcgTableValues>({} as WcgTableValues);

  resetSelectedRows = output<void>();
  delete = output<void>();
  bulkEdit = output<void>();

  readonly $totalSelectedRows = computed(() => {
    return this.selectedRows()?.length || 0;
  });

  readonly exportMenuOpen = signal(false);

  toggleExport(event: MouseEvent) {
    event.stopPropagation();
    this.exportMenuOpen.set(!this.exportMenuOpen());
  }

  @HostListener('document:click', ['$event'])
  closeDropdownOnClickOutside(_: MouseEvent) {
    this.exportMenuOpen.set(false);
  }

  handleExport(type: string): void {
    const exportData = buildExportData(
      this.wcgTableValues(),
      type,
      this._injector,
    );
    const data = createProcessingData(this.getMessage('export'));

    const exportRequest: ExportRequest = {
      url: this.apiURL(),
      wcgTableValues: exportData,
    };
    const args = this.exportArgs();
    // Only include args in the export request if they are defined and not empty
    if (args && Object.keys(args).length > 0) {
      exportRequest.args = args;
    }

    this._downloadsExportFacadeService.initiateExport(exportRequest, data);
  }

  getMessage(type: string): string {
    const selectedCount = this.selectedRows().length;
    const pluralDocs = `${this.$totalSelectedRows()} documents`;
    const singularDocTitle =
      (this.selectedRows()[0] as { title?: string; file_name?: string })
        ?.title ||
      (this.selectedRows()[0] as { title?: string; file_name?: string })
        ?.file_name;

    switch (type) {
      case 'download': {
        const downloadMessages = new Map<number, string>([
          [0, DOWNLOAD_MESSAGE_ONE],
          [1, DOWNLOAD_MESSAGE_TWO],
          [2, DOWNLOAD_MESSAGE_THREE],
        ]);

        const messageKey =
          selectedCount === 0 ? 0 : selectedCount === 1 ? 1 : 2;
        return downloadMessages.get(messageKey) || '';
      }

      case 'export':
        return EXPORT_MESSAGE;

      case 'delete':
        if (selectedCount === 0) {
          return `Are you sure you want to delete all ${pluralDocs}? You cannot revert these changes once you delete.`;
        } else if (selectedCount === 1) {
          return `Are you sure you want to delete ${singularDocTitle}? You cannot revert this change once you delete.`;
        }
        return `Are you sure you want to delete the ${selectedCount} documents listed below?`;

      default:
        return 'Are you sure?';
    }
  }

  /**
   * Handles the download action for selected rows or all rows if none are selected.
   * Constructs the download request and initiates the download process via the DownloadsExportFacadeService.
   * If no rows are selected, it sends an empty array to indicate that all rows should be downloaded.
   */
  handleDownload() {
    const request = {
      study_id: this.studyId(),
      entity_type: this.entityType(),
      entity_id: this.entityId(),
      ids:
        (this.selectedRows() as { id?: string }[])
          ?.map((row: { id?: string }) => row.id)
          .filter((id): id is string => id != null) ?? [],
      is_outcome: this.isOutcome(),
    };

    const data = createProcessingData(this.getMessage('download'));

    this._downloadsExportFacadeService.initiateDownload(request, data);
  }
}
